import * as moment from 'moment';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { fileDownload } from '@shared';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import {
  concatMap,
  catchError,
  exhaustMap,
  map,
  switchMap,
} from 'rxjs/operators';

import { BossService } from '../services/boss.service';
import { BossActions } from './actions';
import { BossItem } from '../models';

@Injectable()
export class BossEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly bossService: BossService,
    private readonly toastrService: ToastrService,
  ) {}

  loadReports$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(BossActions.GetItems.request),
      switchMap(() =>
        this.bossService.getItems().pipe(
          map(({ items }) => BossActions.GetItems.success({ items })),
          catchError((err: HttpErrorResponse) => {
            this.toastrService.error(err?.message);
            return of(BossActions.failure({ errorMessage: err?.message }));
          }),
        ),
      ),
    ),
  );

  refresh$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(BossActions.Refresh.request),
      concatMap(({ item }) =>
        this.bossService.refresh(item.period).pipe(
          map((responseItem) =>
            BossActions.Refresh.success({
              item: {
                id: item.period,
                changes: {
                  ...responseItem,
                  isRefreshing: false,
                },
              },
            }),
          ),
          catchError((err: HttpErrorResponse) => {
            this.toastrService.error(err?.message);
            return of(
              BossActions.failure({ item, errorMessage: err?.message }),
            );
          }),
        ),
      ),
    ),
  );

  download$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(BossActions.Download.request),
      exhaustMap(({ item }) =>
        this.bossService.download(item.period).pipe(
          map((file: ArrayBuffer) => {
            fileDownload(file, this.getReportFileName(item));
            return BossActions.Download.success({ item });
          }),
          catchError((err: HttpErrorResponse) => {
            this.toastrService.error(err?.message);
            return of(
              BossActions.failure({ item, errorMessage: err?.message }),
            );
          }),
        ),
      ),
    ),
  );

  getReportFileName(report: BossItem) {
    return `boss-stats-${report.period}-${moment(
      report.dataLastCollected,
    ).format('YYYYMMDDHHmm')}.xlsx`;
  }
}
